import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  DollarSign,
  Briefcase,
  CheckCircle,
  ChevronRight,
  Users,
  Share2,
  HelpCircle,
  Send,
} from "lucide-react";
import { jobsData, Job } from "../data/jobsData";

const JobDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [activeSection, setActiveSection] = useState("overview");
  // const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const foundJob = jobsData.find((j) => j.id === id);
    if (foundJob) {
      setJob(foundJob);
    } else {
      navigate("/careers");
    }
  }, [id, navigate]);

  const getCategoryStyles = (color: string) => {
    switch (color) {
      case "purple":
        return "bg-[#8B52FF]/10 text-[#8B52FF] border-[#8B52FF]/20";
      case "coral":
        return "bg-[#E05A6A]/10 text-[#E05A6A] border-[#E05A6A]/20";
      case "mixed":
        return "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF] border-[#8B52FF]/20";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const handleApply = () => {
    navigate(`/careers/apply/${job?.id}`, {
      state: { jobId: job?.id, jobTitle: job?.title },
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${job?.title} at Unimerg`,
          text: job?.description,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  // const handleSave = () => {
  //   setIsSaved(!isSaved);
  // };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8B52FF]"></div>
      </div>
    );
  }

  const navItems = [
    { id: "overview", label: "Overview" },
    { id: "responsibilities", label: "Responsibilities" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#FAFAFA]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238B52FF' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Gradient Orbs */}
        <motion.div
          className="absolute top-20 left-[10%] w-[500px] h-[500px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(139, 82, 255, 0.15) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 right-[10%] w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(224, 90, 106, 0.12) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.4, 0.3] }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm mb-8"
          >
            <Link
              to="/"
              className="text-gray-500 hover:text-[#8B52FF] transition-colors"
            >
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <Link
              to="/careers"
              className="text-gray-500 hover:text-[#8B52FF] transition-colors"
            >
              Careers
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-900 font-medium">{job.title}</span>
          </motion.nav>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            {/* Left: Job Info */}
            <div className="flex-1">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${getCategoryStyles(job.categoryColor)}`}
              >
                <Briefcase className="w-4 h-4" />
                {job.category}
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mt-6 mb-6"
              >
                {job.title}
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap items-center gap-4 mb-6"
              >
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
                  <DollarSign className="w-5 h-5 text-[#8B52FF]" />
                  <span className="font-semibold text-gray-900">
                    {job.payRange}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
                  <MapPin className="w-5 h-5 text-[#E05A6A]" />
                  <span className="text-gray-700">{job.locationType}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
                  <Clock className="w-5 h-5 text-[#8B52FF]" />
                  <span className="text-gray-700">{job.schedule}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
                  <Users className="w-5 h-5 text-[#E05A6A]" />
                  <span className="text-gray-700">
                    {job.estimatedHoursPerWeek}
                  </span>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg text-gray-600 leading-relaxed max-w-2xl"
              >
                {job.description}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-2 mt-6"
              >
                {job.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* Right: Action Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:w-80 flex-shrink-0"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sticky top-24">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {job.payRange}
                  </div>
                  <p className="text-gray-500 text-sm">
                    Competitive hourly rate
                  </p>
                </div>

                {/* <button
                  onClick={handleApply}
                  className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold text-lg shadow-lg hover:shadow-xl hover:shadow-[#8B52FF]/25 transition-all duration-300 mb-4"
                >
                  Apply Now
                </button> */}

                <div className="flex gap-3">
                  <button
                    onClick={handleApply}
                    className={`flex-1 py-3 px-4 rounded-xl border font-medium transition-all flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white
                     `}
                  >
                    <Send className="w-5 h-5" />
                    Apply
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-gray-700 font-medium hover:border-[#8B52FF]/50 transition-all flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Share
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">
                        No experience required
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">Training provided</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">Weekly payments</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">Work from anywhere</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <nav className="sticky top-16 lg:top-20 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeSection === item.id
                    ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="flex-1 space-y-16">
              {/* Overview */}
              <div id="overview" className="scroll-mt-32">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    About This Role
                  </h2>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {job.overview}
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {job.longDescription}
                    </p>
                  </div>

                  <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-[#8B52FF]/5 to-[#E05A6A]/5 border border-[#8B52FF]/10">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-[#8B52FF]" />
                      Types of Projects
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {job.projectTypes.map((type, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 rounded-lg bg-white text-gray-700 text-sm border border-gray-100 shadow-sm"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Responsibilities */}
              <div id="responsibilities" className="scroll-mt-32">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    What You'll Do
                  </h2>
                  <div className="space-y-3">
                    {job.responsibilities.map((resp, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] mt-2 flex-shrink-0" />
                        <p className="text-gray-700">
                          <span className="font-medium text-gray-900">
                            {resp.title}:
                          </span>{" "}
                          {resp.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block lg:w-80 flex-shrink-0">
              <div className="sticky top-36 space-y-6">
                <div className="bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] rounded-2xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">Ready to Apply?</h3>
                  <p className="text-white/80 text-sm mb-4">
                    Join thousands of contributors already earning with Unimerg.
                  </p>
                  <button
                    onClick={handleApply}
                    className="w-full py-3 px-4 rounded-xl bg-white text-[#8B52FF] font-semibold hover:shadow-lg transition-all"
                  >
                    Apply Now
                  </button>
                </div>

                <div className="bg-gray-50 rounded-2xl border border-gray-300 p-6">
                  <HelpCircle className="w-8 h-8 text-[#8B52FF] mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Have Questions?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Our team is here to help you with any questions about this
                    role.
                  </p>
                  <Link
                    to="/help"
                    className="text-sm text-[#8B52FF] hover:text-[#E05A6A] font-medium transition-colors"
                  >
                    Contact Support →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Join our global community of contributors and start earning while
              shaping the future of AI.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleApply}
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold text-lg hover:shadow-xl hover:shadow-[#8B52FF]/25 transition-all"
              >
                Apply for {job.title}
              </button>
              <Link
                to="/careers"
                className="px-8 py-4 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/10 transition-all"
              >
                View All Positions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;
